import { SharedAuthEvent } from '@loomhq/enums';
import { Link, Spacer, Text } from '@loomhq/lens';
import {
  decode,
  encode,
  generateRSAKeys,
  privateDecrypt,
} from '@loomhq/shared-auth-browser';
import React, { Fragment, useEffect } from 'react';
import uuid from 'uuid';

import * as analytics from '../../../../utilities/analytics';
import * as loggerx from '../../../../utilities/loggerx';
import OnSessionRequestToken from './OnSessionRequestToken.graphql';
import { EMBED_LOGGED_OUT_SCREEN_VIEWED } from '../../../../constants/events';
import { LOGIN_PAGE, LOOM_URI } from '../../../../constants/routes';
import { useFlagIsActivated } from '../../../../hooks/featureFlag';
import { getGraphQLClient } from '../../../../utilities/graphql';
import { APP_SOURCE_SLACK_DESKTOP } from '../../../../../../shared/constants/analytics';
import { ROLLOUT_SLACK_APP_SHARED_AUTH } from '../../../../../../shared/constants/featureFlag';
import { getloginUrlWithRedirect } from '../../../../../../shared/utilities/url';

import Button from 'components/ui-components/button';

const SESSION_REQUEST_CALLBACK_FAILED = 'failure';

/*
 * This component is responsible for 3 code paths:
 * 1. The viewer is trying to view a video that has email gating and they are not
 *    in this list.
 * 2. The viewer is trying to view a video that has email gating and they are logged
 *    out.
 * 3. The viewer is trying to view a video that has email gating, they are in this
 *    list, but their email is not verified.
 *
 * We always surface this UI from path #3 if the user does not have their email
 * verified in an attempt to get them to verify even if they are not in the list
 * of permitted viewers.
 *
 * It is used in 2 places:
 * 1. At the top-level of the share video page to gate all page interaction.
 * 2. Within the embed video player in case the video is embedded on an external
 *    site
 */

const LoggedOutScreen = ({
  redirect,
  isMainVideoOnSharePage,
  isFromSlackDesktopApp,
  videoId,
}) => {
  const isSlackSharedAuthEnabled = useFlagIsActivated({
    flag: ROLLOUT_SLACK_APP_SHARED_AUTH,
    activationValues: [true],
  });

  const modalBody = isMainVideoOnSharePage
    ? 'Public viewing has been disabled by the video owner. Login to view or request access.'
    : 'Due to the privacy settings for this video, it cannot be played here at this time.';

  let modalAction;

  useEffect(() => {
    const props = {};

    if (isSlackSharedAuthEnabled !== undefined) {
      if (isFromSlackDesktopApp) {
        props.source = APP_SOURCE_SLACK_DESKTOP;
      }

      props.slack_shared_auth_enabled = isSlackSharedAuthEnabled;
      analytics.track(EMBED_LOGGED_OUT_SCREEN_VIEWED, props);
    }
  }, [isSlackSharedAuthEnabled, isFromSlackDesktopApp]);

  if (isMainVideoOnSharePage) {
    modalAction = (
      <Fragment>
        <Button
          onClicked={() => {
            window.location = getloginUrlWithRedirect(
              redirect,
              'Please Login to view this video',
              LOOM_URI + LOGIN_PAGE
            );
          }}
          capitalize={false}
          buttonType="rp-primary-2"
          value="Login"
        />
        <Spacer top="medium">
          <Link href={`${LOGIN_PAGE}?signup=1`}>Create an Account</Link>
        </Spacer>
      </Fragment>
    );
  } else if (isSlackSharedAuthEnabled && isFromSlackDesktopApp) {
    modalAction = (
      <Button
        capitalize={false}
        buttonType="rp-primary-3"
        value="View it on Loom"
        onClicked={() => viewOnLoomSharedAuthClicked(videoId)}
      />
    );
  } else {
    modalAction = (
      <a
        rel="noopener noreferrer"
        target="_blank"
        href={`${LOOM_URI}/share/${videoId}`}
      >
        <Button
          capitalize={false}
          buttonType="rp-primary-3"
          value="View it on Loom"
        />
      </a>
    );
  }

  return (
    <Fragment>
      <Text
        className="mb:small"
        alignment="center"
        fontWeight="bold"
        size="xlarge"
      >
        Sorry
      </Text>
      <div className="loom-permissions-modal-body">{modalBody}</div>
      {modalAction}
    </Fragment>
  );
};

const viewOnLoomSharedAuthClicked = async videoId => {
  const loginAttemptId = uuid.v4();
  const trackingIds = analytics.getAnalyticsIds();

  const track = eventName => {
    analytics.track(eventName, {
      source: APP_SOURCE_SLACK_DESKTOP,
      loginAttemptId,
      anonymousId: trackingIds.anonId,
      deviceId: trackingIds.deviceId,
    });
  };

  track(SharedAuthEvent.Initiated);

  const { publicKeyAsPem, privateKey } = await generateRSAKeys();
  const encodedPublicKey = encode(publicKeyAsPem);

  const gqlClient = getGraphQLClient();
  const subscription = gqlClient
    .subscribe({
      variables: { encodedPublicKey },
      query: OnSessionRequestToken,
    })
    .subscribe(body => {
      subscription.unsubscribe();
      gqlClient.stop();

      sessionRequestCallback({
        body,
        trackingIds,
        loginAttemptId,
        privateKey,
        track,
      });
    });

  track(SharedAuthEvent.GraphQLSubscribed);

  const queryParams = {
    anon_id: trackingIds.anonID,
    device_id: trackingIds.deviceID,
    app_source: APP_SOURCE_SLACK_DESKTOP,
    ak: encodedPublicKey,
    login_attempt_id: loginAttemptId,
    redirect_after: `${LOOM_URI}/share/${videoId}`,
  };
  const queryParamStrings = Object.keys(queryParams).map(
    key => `${key}=${queryParams[key]}`
  );
  const url = `${LOOM_URI}/shared-auth/login?${queryParamStrings.join('&')}`;

  window.open(url);
};

const sessionRequestCallback = async ({
  body,
  trackingIds,
  loginAttemptId,
  privateKey,
  track,
}) => {
  track(SharedAuthEvent.GraphQLHeardBack);

  const { token: encryptedEncodedSRT, status } = body.data.sessionRequestToken;

  if (status === SESSION_REQUEST_CALLBACK_FAILED) {
    loggerx.error(Error('Session request callback failed.'), {
      message: `Server response: ${status}`,
    });

    return;
  }

  track(SharedAuthEvent.SessionRequestTokenReceived);

  const decryptedSRT = await privateDecrypt(
    privateKey,
    decode(encryptedEncodedSRT)
  );

  const queryParams = {
    anon_id: trackingIds.anonID,
    device_id: trackingIds.deviceID,
    login_attempt_id: loginAttemptId,
  };
  const queryParamStrings = Object.keys(queryParams).map(
    key => `${key}=${queryParams[key]}`
  );

  fetch(`${LOOM_URI}/api/auth/session?${queryParamStrings.join('&')}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      srt: encode(decryptedSRT),
    }),
  }).then(resp => {
    if (resp.status >= 400) {
      loggerx.error(Error('Fetch session request token failed.'), {
        message: `Response: [${resp.status}] ${resp.message}`,
      });

      return;
    }

    track(SharedAuthEvent.LoginSuccess);

    window.location.reload();
  });
};

export default LoggedOutScreen;
